/**
 * One error (user, 20/10/2026: "I can't understand anything here — why did
 * it fail?"): one line — when (Danish first), the kind, where, what
 * happened with WHY under it, Slack, and the state in words ("Open · 33
 * times, last 08:22"; 21/10/2026 "Resolved · 09:31 texts filled", the row
 * calmer). A click opens three plain blocks — What happened,
 * Why, What to do — with the buttons that act on it (open the container,
 * fill the texts again) and the details (the drop, the repeats, Slack).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import type { DropGenErrorRow } from '@trafficpulse/shared';

import { Badge, Tooltip } from '@/components/ui';
import { cn } from '@/lib/utils';

import { instantDk, instantText } from '../../../utils/clock/dk-label';
import { ContainerChip } from '../../shared/ContainerChip';
import { useCountryClock } from '../../shared/time/country-clock-context';
import { alertWorthy } from '../errors-facts';
import { BADGE_TONE, kindWords, shortResolution, whereText } from '../errors-words';
import { ErrorDetail } from './ErrorDetail';

export const GRID = 'grid grid-cols-[118px_128px_minmax(150px,1fr)_minmax(300px,2.4fr)_92px_128px] items-start gap-3 px-5';

const dayLabel = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

const SlackCell: FC<{ row: DropGenErrorRow }> = ({ row }) => {
  if (!alertWorthy(row)) return <Tooltip content="Traced here only — a read failure or the channel that failed is never sent to Slack."><span className="text-muted-foreground">here only</span></Tooltip>;
  if (row.notifiedAt) return <Tooltip content={`Delivered to Slack at ${instantDk(row.notifiedAt).label} Danish time.`}><span className="text-success">✓ sent {instantDk(row.notifiedAt).label}</span></Tooltip>;
  if (row.notifyError) return <Tooltip content={`Not delivered: ${row.notifyError}`}><span className="font-medium text-destructive">✗ not sent</span></Tooltip>;
  return <Tooltip content="Waiting for the next alert pass (every minute), or Slack is off for this country."><span className="text-muted-foreground">waiting</span></Tooltip>;
};

/** The swap made from this error, if any: "swapped 09:40 · waits for the fill" until a fill resolves it. */
const swappedAt = (row: DropGenErrorRow): string | null => {
  const at = (row.detail?.swap as { at?: unknown } | undefined)?.at;
  return typeof at === 'string' ? at : null;
};

/** "Open" / "Open · critical" / "Resolved", and under it "33 times · last 08:22", "09:31 · texts filled" or "swapped 09:40 · waits for the fill". */
const StateCell: FC<{ row: DropGenErrorRow }> = ({ row }) => {
  const critical = kindWords(row.kind).tone === 'critical';
  const swapped = swappedAt(row);
  // the same failure came back after the swap (a fill with the new group failed too)
  const failedAgain = swapped !== null && row.lastAt > swapped;
  if (row.resolvedAt)
    return (
      <span className="flex min-w-0 flex-col items-start gap-0.5">
        <Tooltip content={`Resolved ${instantDk(row.resolvedAt).label} Danish time — ${row.resolution ?? ''}`}><Badge tone="success">✓ Resolved</Badge></Tooltip>
        <span className="max-w-full truncate text-[10.5px] text-success" title={row.resolution ?? undefined}>{instantDk(row.resolvedAt).label} · {shortResolution(row.resolution)}</span>
      </span>
    );
  return (
    <span className="flex flex-col items-start gap-0.5">
      <Badge tone={critical ? 'destructive' : 'secondary'}>{critical ? 'Open · critical' : 'Open'}</Badge>
      {swapped && !failedAgain ? <span className="text-[10.5px] text-primary">swapped {instantDk(swapped).label} · waits for the fill</span> : null}
      {failedAgain ? <span className="text-[10.5px] font-medium text-warning">failed again after the swap · {instantDk(row.lastAt).label}</span> : null}
      {!swapped && row.repeats > 1 ? <span className="text-[10.5px] text-muted-foreground">{row.repeats} times · last {instantDk(row.lastAt).label}</span> : null}
    </span>
  );
};

export const ErrorRow: FC<{ row: DropGenErrorRow; showDate: boolean; countryKey: string; onEditContainer?: (containerId: string) => void }> = ({ row, showDate, countryKey, onEditContainer }) => {
  const [open, setOpen] = useState(false);
  const clock = useCountryClock();
  const w = kindWords(row.kind);
  const dk = instantDk(row.at);
  return (
    <div className={cn('border-b border-border/30 last:border-b-0', row.resolvedAt && !open && 'opacity-60 transition-opacity hover:opacity-100')} data-testid="error-row" data-kind={row.kind} data-state={row.resolvedAt ? 'resolved' : 'open'}>
      <button type="button" aria-expanded={open} onClick={() => setOpen((v) => !v)} className={cn(GRID, 'w-full py-2.5 text-left text-xs transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary', open && 'bg-muted/30')}>
        <span className="flex flex-col font-mono tabular-nums">
          <span>{showDate ? `${dayLabel(dk.date)} · ` : ''}{dk.label} 🇩🇰</span>
          <span className="text-[10.5px] text-muted-foreground">{clock.tz ? instantText(row.at, clock).split(' · ')[1] : ''}</span>
        </span>
        <span>
          <Tooltip content={w.hint}>
            <Badge tone={BADGE_TONE[w.tone]}>{w.label}</Badge>
          </Tooltip>
        </span>
        <span className="flex min-w-0 flex-col gap-0.5">
          {row.containerName ? <ContainerChip name={row.containerName} color="#64748b" className="!self-start !px-2 !text-[10.5px]" /> : null}
          <span className="truncate text-muted-foreground" title={whereText(row)}>{row.containerName ? whereText(row).replace(row.containerName, '').replace(/^ · /, '') || ' ' : whereText(row)}</span>
        </span>
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className={cn('font-medium', open ? 'whitespace-normal' : 'truncate')} title={row.headline}>{row.headline}</span>
          <span className={cn('text-muted-foreground', open ? 'hidden' : 'truncate')} title={row.message}>
            <span className="font-semibold text-foreground/70">Why: </span>
            {row.message}
          </span>
        </span>
        <span className="font-mono tabular-nums"><SlackCell row={row} /></span>
        <span className="flex items-start justify-between gap-1"><StateCell row={row} /><ChevronDown className={cn('mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform', open && 'rotate-180')} aria-hidden /></span>
      </button>
      {open ? <ErrorDetail row={row} countryKey={countryKey} onEditContainer={onEditContainer} /> : null}
    </div>
  );
};
